import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { rememberNewClaw } from "../../hooks/useClaws";
import { submitOnboarding } from "../../hooks/useOnboarding";
import type { SubmitErrorCode } from "../../types/onboarding";
import { OnboardingProvider, useOnboardingDraft } from "./OnboardingContext";
import { RequestedAppsLinks, useServableApps } from "./RequestedApps";
import styles from "./OnboardingWizard.module.css";
import { workflowStepIncludedForTier } from "../../config/workflows";
import { AboutYouStep } from "./steps/AboutYouStep";
import { AgentStep } from "./steps/AgentStep";
import { AiSetupStep } from "./steps/AiSetupStep";
import { ChannelStep } from "./steps/ChannelStep";
import { ReviewStep } from "./steps/ReviewStep";
import { WorkflowStep } from "./steps/WorkflowStep";

type StepKey = "agent" | "about" | "channel" | "workflow" | "ai" | "review";

type StepDef = {
  key: StepKey;
  label: string;
  Component: (props: StepProps) => JSX.Element;
  // Present = the step only renders when the user's tier passes the check.
  includedForTier?: (tier: string | null) => boolean;
};

const STEP_DEFS: ReadonlyArray<StepDef> = [
  { key: "agent", label: "Agent", Component: AgentStep },
  { key: "about", label: "About you", Component: AboutYouStep },
  { key: "channel", label: "Channel", Component: ChannelStep },
  { key: "workflow", label: "Workflow", Component: WorkflowStep, includedForTier: workflowStepIncludedForTier },
  { key: "ai", label: "AI", Component: AiSetupStep },
  { key: "review", label: "Review", Component: ReviewStep },
];

const stepsForTier = (tier: string | null): ReadonlyArray<StepDef> =>
  STEP_DEFS.filter((s) => s.includedForTier === undefined || s.includedForTier(tier));

export type StepProps = {
  onNext: () => void;
  onBack: () => void;
  // Leaves onboarding entirely (used by the first step in place of Back).
  onCancel: () => void;
  canBack: boolean;
  onJumpTo: (key: StepKey) => void;
  onSubmit: () => Promise<void>;
  submitting: boolean;
  submitError: string | null;
};

export const OnboardingWizard = (): JSX.Element => (
  <OnboardingProvider>
    <Inner />
  </OnboardingProvider>
);

// One-shot count of the caller's OWNED OpenClaws. We avoid `useClaws`
// here on purpose: that hook opens an SSE stream that the wizard does
// not need (we're not rendering the live list, just enforcing budget at
// route entry). Shared instances (MANAGER/MEMBER role) don't consume the
// caller's budget, so they're excluded from the count.
const useOwnedClawCount = (): {
  status: "loading" | "ready";
  count: number;
  // First owned tenant, so the at-budget screen can start a connect against
  // the instance they already have.
  tenantId: string | null;
} => {
  const [status, setStatus] = useState<"loading" | "ready">("loading");
  const [count, setCount] = useState<number>(0);
  const [tenantId, setTenantId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/claws", { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 200) {
          const body = (await res.json()) as Array<{ role?: string; tenantId?: string }>;
          const owned = body.filter((c) => (c.role ?? "OWNER") === "OWNER");
          setCount(owned.length);
          setTenantId(owned[0]?.tenantId ?? null);
        } else {
          setCount(0);
          setTenantId(null);
        }
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setCount(0);
        setTenantId(null);
        setStatus("ready");
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { status, count, tenantId };
};

const Inner = (): JSX.Element => {
  const { user, status: authStatus } = useAuth();
  const navigate = useNavigate();
  const { reset } = useOnboardingDraft();
  const { count: visibleCount, status: clawsStatus, tenantId: ownedTenantId } = useOwnedClawCount();
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { draft } = useOnboardingDraft();
  const requestedApps = user?.requestedIntegrations ?? [];
  // Same gate as the steps: nothing servable, no "Connect your apps" screen —
  // otherwise a fully-retired set would hide the instance-limit copy behind a
  // heading with nothing under it.
  const apps = useServableApps(requestedApps);
  const steps = stepsForTier(user?.tier ?? null);
  const totalSteps = steps.length;

  const userBudget = user?.userBudget ?? 1;
  // `/api/claws` now returns locked rows alongside unlocked, so the
  // visible count is already the budget-consuming count, no need to
  // add `pendingInstance` on top.
  const ownedCount = visibleCount;
  const atBudget = ownedCount >= userBudget;
  // Both fetches must land before any budget gate fires: with only the
  // claws count settled, a zero-budget user's userBudget still reads as
  // the ?? 1 fallback and the wizard would render, then abruptly swap to
  // the purchase screen when /api/me resolves.
  const budgetSettled = clawsStatus === "ready" && authStatus !== "loading";

  const handleNext = (): void => {
    if (stepIndex < totalSteps - 1) setStepIndex(stepIndex + 1);
  };

  const handleBack = (): void => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
    setSubmitError(null);
  };

  const handleJumpTo = (key: StepKey): void => {
    const idx = steps.findIndex((s) => s.key === key);
    if (idx >= 0) setStepIndex(idx);
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    const result = await submitOnboarding(draft);
    setSubmitting(false);
    if (result.ok) {
      reset();
      // Straight to the dashboard: its locked card carries the walkthrough,
      // the onboarding-call CTA and any apps pinned to the invite.
      rememberNewClaw(result.claw);
      navigate("/dashboard/overview", { replace: true });
      return;
    }
    setSubmitError(humanizeError(result.code, result.message));
  };

  // Hold the route blank until the gates can be decided. Rendering the
  // wizard first and swapping to a limit/purchase card when the data
  // lands would let the user start filling steps they can't submit.
  if (!budgetSettled) {
    return <div className={styles.page} />;
  }

  if (budgetSettled && userBudget === 0) {
    return (
      <div className={styles.page}>
        <section className={styles.successCard}>
          <span className={styles.successMark} aria-hidden>🦞</span>
          <h2 className={styles.successTitle}>Your account doesn't include its own instance yet.</h2>
          <p>
            You're a collaborator on a shared OpenClaw. To launch an instance of your own,
            purchase one first and it'll unlock onboarding here automatically.
          </p>
          <div className={styles.footer}>
            {(user?.purchaseUrl ?? "").length > 0 ? (
              <a
                className={styles.primaryBtn}
                href={user?.purchaseUrl}
                target="_blank"
                rel="noreferrer"
              >
                Get your own OpenClaw
              </a>
            ) : null}
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => navigate("/dashboard/overview")}
            >
              Back to dashboard
            </button>
          </div>
        </section>
      </div>
    );
  }

  // Already onboarded. When an admin pinned apps to this customer's invite,
  // anyone coming back through "Start onboarding" gets them listed here
  // rather than a dead end with no reminder of what they still need to connect.
  if (budgetSettled && atBudget && apps.visible.length > 0) {
    return (
      <div className={styles.page}>
        <section className={styles.successCard}>
          <span className={styles.successMark} aria-hidden>🦞</span>
          <h2 className={styles.successTitle}>Connect your apps.</h2>
          <p>
            These are the ones set up for your account - open each and follow the connect flow.
            Your agent picks them up as soon as they're connected.
          </p>
          <RequestedAppsLinks {...apps} tenantId={ownedTenantId} />
          <p className={styles.limitNote}>
            You're already using your {userBudget === 1 ? "instance" : `${userBudget} instances`}, so
            there's no new onboarding to run. Delete an existing instance, or ask an admin to expand
            your capacity, to start another.
          </p>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => navigate("/dashboard/overview")}
            >
              Back to dashboard
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (budgetSettled && atBudget) {
    return (
      <div className={styles.page}>
        <section className={styles.successCard}>
          <span className={styles.successMark} aria-hidden>🦞</span>
          <h2 className={styles.successTitle}>You're at your instance limit.</h2>
          <p>
            You've reached your {userBudget}-instance limit. Delete an existing instance, or wait
            for an admin to expand your capacity, before starting another onboarding.
          </p>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => navigate("/dashboard/overview")}
            >
              Back to dashboard
            </button>
          </div>
        </section>
      </div>
    );
  }

  // steps is never empty (only the workflow step is tier-gated).
  const currentStep = steps[stepIndex] ?? steps[0]!;
  const Step = currentStep.Component;

  return (
    <div className={styles.page}>
      <header>
        <span className={styles.eyebrow}>Get started</span>
        <h1 className={styles.title}>Set up your OpenClaw</h1>
        <p className={styles.subtitle}>
          A few quick questions so we can spin up your instance and tailor it to how you'll use it.
        </p>
      </header>
      <div className={styles.progress} role="progressbar" aria-valuenow={stepIndex + 1} aria-valuemin={1} aria-valuemax={totalSteps}>
        {steps.map((s, i) => {
          const cls =
            i === stepIndex
              ? `${styles.progressDot} ${styles.progressDotActive}`
              : i < stepIndex
                ? `${styles.progressDot} ${styles.progressDotDone}`
                : styles.progressDot;
          return <span key={s.key} className={cls} aria-label={s.label} />;
        })}
      </div>
      <Step
        onNext={handleNext}
        onBack={handleBack}
        onCancel={() => navigate("/dashboard/overview")}
        canBack={stepIndex > 0}
        onJumpTo={handleJumpTo}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitError={submitError}
      />
    </div>
  );
};

const humanizeError = (code: SubmitErrorCode, fallback: string): string => {
  switch (code) {
    case "CAP":
      return "You've reached your instance limit. Add capacity to start another.";
    case "DUPLICATE":
      return "An instance with this agent name already exists. Try a different name.";
    case "INVALID":
      return fallback;
    case "UNAUTHORIZED":
      return "Your session expired. Refresh the page and try again.";
    default:
      return fallback || "Something went wrong. Please try again.";
  }
};
